import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

/**
 * Mibeko Theme Provider — light / dark / system-follow.
 *
 * Priority order at first mount :
 *   1. explicit user choice in localStorage ('mibeko_theme' = 'light' | 'dark')
 *   2. otherwise : system preference (prefers-color-scheme: dark) → dark
 *   3. otherwise : light
 *
 * On every change :
 *   - localStorage is updated
 *   - <html data-theme="…"> is set (so CSS variables switch)
 */

const STORAGE_KEY = 'mibeko_theme';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

function readInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (err) {
    console.warn('[Theme] localStorage read failed:', err?.message || err);
  }
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function safeWriteTheme(next) {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch (err) {
    console.warn('[Theme] localStorage write failed:', err?.message || err);
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    safeWriteTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark';
      safeWriteTheme(next);
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Stable context value : consumers only re-render when theme changes.
  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
